/**
 * Per-Meta-campaign detail route — the Meta sibling of
 * ``routes/_authenticated/_layout/campaigns/google/$campaignslug/_slug/index.tsx``.
 *
 * Renders the same sidebar (``<CampaignSidebar>``) on the left and Meta's tabbed detail panel
 * (``<MetaCampaignDetailPanel>``) on the right, so the unified ``/campaigns`` surface keeps a
 * consistent layout regardless of platform (#4173).
 *
 * Addressed by the opaque ``metaCampaignSlug`` URL token (#4659), mirroring Google's ``$campaignslug``
 * — no enumerable integer id in the URL.
 *
 * The wrapping (TabCardPanel + grid + filter pills) is duplicated from the Google slug route; we
 * accept the duplication rather than refactoring the Google route in this PR. Future work can lift
 * the shared layout into a parent route file if more platforms land.
 */

import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';

import { campaignDataAtom, metaCampaignDataAtom } from '@/store';
import { CampaignSidebar, type PlatformFilter } from '@/components/campaign/campaign-sidebar';
import { CampaignSidebarToolbar } from '@/components/campaign/campaign-sidebar-toolbar';
import { useCampaignSidebarFilters } from '@/hooks/useCampaignSidebarFilters';
import { MetaCampaignDetailHeader, MetaCampaignDetailPanel } from '@/components/campaign/meta/meta-campaign-detail-panel';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';

export const Route = createFileRoute('/_authenticated/_layout/campaigns/meta/$metaCampaignSlug')({
  component: MetaCampaignPage,
});

function MetaCampaignPage() {
  const { metaCampaignSlug } = useParams({ from: '/_authenticated/_layout/campaigns/meta/$metaCampaignSlug' });
  const navigate = useNavigate();
  const setMetaCampaignData = useSetAtom(metaCampaignDataAtom);
  const setCampaignData = useSetAtom(campaignDataAtom);

  // Remember this as the last-selected campaign so returning to /campaigns restores it (#4660) —
  // mirrors the Google slug route writing campaignDataAtom. Clearing campaignDataAtom marks Meta as
  // the most recent pick (the Google route clears metaCampaignDataAtom in turn), keeping exactly one
  // of the two non-null.
  useEffect(() => {
    setMetaCampaignData(metaCampaignSlug);
    setCampaignData(null);
  }, [metaCampaignSlug, setMetaCampaignData, setCampaignData]);

  // Campaign filter state persisted via user settings — the same hook Google's slug route uses
  // (#4446), replacing the hand-rolled useState trio that reset on every navigation (#6163 P2,
  // #6182). Single-value platformFilter derives from the multi-select set, Google's exact rule.
  const { filterTypes, filterStatuses, filterPlatforms, updateFilters } = useCampaignSidebarFilters();
  const platformFilter: PlatformFilter =
    filterPlatforms.size === 1 && filterPlatforms.has('google') ? 'google' :
    filterPlatforms.size === 1 && filterPlatforms.has('meta') ? 'meta' :
    'all';

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3" />
      </div>

      <div className="flex-1">
        {/* Top-of-page Meta header — full-width failure banner + post-generation approval flow,
            mirroring where Google's route places ``<CampaignApprovalFlow>`` above its 12-col layout.
            The generation-progress card is NOT here: it renders inside the content panel (col-span-9)
            so it sits in the content column like Google's generating banner, not full-width.
            Keyed by slug: the router reuses this tree on params-only navigation, and the header's
            first-observed-progress-state ref (#6163 P1-13) must reset per campaign — an unkeyed
            render carried a completed campaign's verdict into a generating one, suppressing the
            invalidations that stream its ads in (#6179). */}
        <MetaCampaignDetailHeader key={metaCampaignSlug} slug={metaCampaignSlug} />

        <div className="grid grid-cols-12 gap-1">
          {/* Left: Campaigns List Tab Card (25%) — same component the Google route uses */}
          <div className="col-span-3 sticky top-0 self-start h-[calc(100vh-120px)]">
            <TabCardPanel
              tabs={[
                { id: 'all', label: 'Campaigns', isActive: true },
                { id: 'new', label: '+ Add', onClick: () => navigate({ to: '/new/campaign' }) },
              ]}
              actions={
                <CampaignSidebarToolbar
                  filterTypes={filterTypes}
                  filterStatuses={filterStatuses}
                  filterPlatforms={filterPlatforms}
                  showPlatforms
                  onFiltersChange={updateFilters}
                />
              }
              cardClassName="overflow-hidden"
              noPadding
            >
              <CampaignSidebar
                filterTypes={filterTypes}
                filterStatuses={filterStatuses}
                platformFilter={platformFilter}
              />
            </TabCardPanel>
          </div>

          {/* Right: Meta campaign detail (75%) */}
          <div className="col-span-9">
            <MetaCampaignDetailPanel slug={metaCampaignSlug} />
          </div>
        </div>
      </div>
    </div>
  );
}
